import { createContext, useContext, useState, ReactNode } from "react";
import { ArtworkRow } from "@/hooks/use-artworks";

export interface CartItem {
  artwork: ArtworkRow;
  quantity: number;
}

interface CartContextValue {
  items: CartItem[];
  addItem: (artwork: ArtworkRow) => void;
  removeItem: (artworkId: string) => void;
  clearCart: () => void;
  totalCount: number;
  totalPrice: number;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);

  const addItem = (artwork: ArtworkRow) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.artwork.id === artwork.id);
      if (existing) return prev; // already in basket — artworks are unique originals
      return [...prev, { artwork, quantity: 1 }];
    });
  };

  const removeItem = (artworkId: string) => {
    setItems((prev) => prev.filter((i) => i.artwork.id !== artworkId));
  };

  const clearCart = () => setItems([]);

  const totalCount = items.length;
  const totalPrice = items.reduce((sum, i) => sum + (i.artwork.price ?? 0) * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, clearCart, totalCount, totalPrice }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
